import { useCallback, useEffect, useState } from 'react';
import { IconCheck, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from '../../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../../utils/bnFormat';
import { ATS_CV_WIZARD_STEPS, getWizardProgress } from '../engines/atsCvWizardSteps.js';
import '@/styles/ats-cv-wizard.scss';

const STEPS_IN_VIEW = 3;

function getCarouselStart(activeStep, totalSteps) {
    if (totalSteps <= STEPS_IN_VIEW) return 0;
    return Math.max(0, Math.min(activeStep - 1, totalSteps - STEPS_IN_VIEW));
}

export default function AtsCvFormWizardNav({ activeStep, form, onStepChange, onNext }) {
    const { t } = useTranslation();
    const { steps, total, journeyPercent } = getWizardProgress(form, activeStep);
    const current = ATS_CV_WIZARD_STEPS[activeStep];
    const stepCount = steps.length;
    const maxCarouselStart = Math.max(0, stepCount - STEPS_IN_VIEW);
    const showCarouselNav = stepCount > STEPS_IN_VIEW;

    const [carouselStart, setCarouselStart] = useState(() => getCarouselStart(activeStep, stepCount));

    useEffect(() => {
        setCarouselStart(getCarouselStart(activeStep, stepCount));
    }, [activeStep, stepCount]);

    // No step is locked — nothing in the CV is required, so any step can be
    // opened at any time. Next still runs through `onNext` so the last step
    // keeps its "jump to the preview" behaviour.
    const handleStepClick = (index) => {
        if (index === activeStep) return;
        if (index === activeStep + 1) {
            onNext();
            return;
        }
        onStepChange(index);
    };

    const scrollCarouselPrev = useCallback(() => {
        setCarouselStart((start) => Math.max(0, start - 1));
    }, []);

    const scrollCarouselNext = useCallback(() => {
        setCarouselStart((start) => Math.min(maxCarouselStart, start + 1));
    }, [maxCarouselStart]);

    const trackStyle = {
        '--step-count': stepCount,
        '--carousel-start': carouselStart,
        width: `${(stepCount / STEPS_IN_VIEW) * 100}%`,
        transform: `translateX(-${(carouselStart / stepCount) * 100}%)`,
    };

    return (
        <div className="ats-cv-wizard ats-cv-wizard-nav">
            <div className="ats-cv-wizard__top">
                <div>
                    <p className="ats-cv-wizard__title">
                        {t(`atsCvMaker.wizard.steps.${current?.key}.label`)}
                        {current?.optional ? (
                            <span className="ml-1 text-[12px] font-medium text-[#868e96]">
                                ({t('atsCvMaker.wizard.optional')})
                            </span>
                        ) : null}
                    </p>
                    <p className="ats-cv-wizard__desc">
                        {t(`atsCvMaker.wizard.steps.${current?.key}.description`)}
                    </p>
                </div>
                <div className="ats-cv-wizard__counter" aria-live="polite">
                    <strong>
                        {toBnDigits(activeStep + 1)}/{toBnDigits(total)}
                    </strong>
                    {t('atsCvMaker.wizard.stepProgress', { percent: toBnDigits(journeyPercent) })}
                </div>
            </div>

            <div
                className="ats-cv-wizard__bar"
                role="progressbar"
                aria-valuenow={journeyPercent}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <div className="ats-cv-wizard__bar-fill" style={{ width: `${journeyPercent}%` }} />
            </div>

            <div className="ats-cv-wizard__steps-carousel">
                {showCarouselNav ? (
                    <button
                        type="button"
                        className="ats-cv-wizard__steps-nav"
                        aria-label={t('atsCvMaker.wizard.prevSteps')}
                        disabled={carouselStart === 0}
                        onClick={scrollCarouselPrev}
                    >
                        <IconChevronLeft size={11} aria-hidden />
                    </button>
                ) : null}

                <div className="ats-cv-wizard__steps-viewport">
                    <div
                        className="ats-cv-wizard__steps-track"
                        style={trackStyle}
                        role="tablist"
                        aria-label={t('atsCvMaker.wizard.tablist')}
                    >
                        {steps.map((step) => {
                            const isActive = step.index === activeStep;
                            const isDone = step.complete && !isActive;

                            return (
                                <button
                                    key={step.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    aria-current={isActive ? 'step' : undefined}
                                    className={[
                                        'ats-cv-wizard__step',
                                        isActive ? 'is-active' : '',
                                        isDone ? 'is-done' : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => handleStepClick(step.index)}
                                    title={t(`atsCvMaker.wizard.steps.${step.key}.description`)}
                                >
                                    <span className="ats-cv-wizard__dot">
                                        {isDone ? (
                                            <IconCheck size={11} aria-hidden />
                                        ) : (
                                            toBnDigits(step.index + 1)
                                        )}
                                    </span>
                                    <span className="ats-cv-wizard__label">
                                        {t(`atsCvMaker.wizard.steps.${step.key}.label`)}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {showCarouselNav ? (
                    <button
                        type="button"
                        className="ats-cv-wizard__steps-nav"
                        aria-label={t('atsCvMaker.wizard.nextSteps')}
                        disabled={carouselStart >= maxCarouselStart}
                        onClick={scrollCarouselNext}
                    >
                        <IconChevronRight size={11} aria-hidden />
                    </button>
                ) : null}
            </div>
        </div>
    );
}
